import React, { useEffect, useMemo, useState } from 'react';
import { HoverInsight } from '../types';
import MetricCard from '../components/MetricCard';
import SheetDataViewer from '../components/SheetDataViewer';
import RecentChangesPanel from '../components/RecentChangesPanel';
import { FileSpreadsheet } from 'lucide-react';
import api from '../lib/api';
import { formatIST } from '../lib/formatDate';
import {
  PieChart, Pie, Cell, Tooltip as ChartTooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';

interface CategoryOpsProps {
  onHover: (insight: HoverInsight | null) => void;
}

const PO_TAB = '3. PO Tracker';
const colIdx = (header: string[], name: string) => header.findIndex((h) => (h || '').toLowerCase().trim() === name.toLowerCase());
const parseCurrency = (s: string) => { const n = parseFloat((s || '').toString().replace(/[^0-9.-]/g, '')); return isNaN(n) ? 0 : n; };
const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;
const COLORS = ['#1d4ed8', '#0ea5e9', '#60a5fa', '#0369a1', '#38bdf8', '#93c5fd', '#94a3b8'];
const STATUS_COLORS: Record<string, string> = { 'On-track': '#10b981', 'Waiting for payment': '#f59e0b', 'Delayed': '#f43f5e' };

export default function CategoryOps({ onHover }: CategoryOpsProps) {
  const [tabs, setTabs] = useState<Record<string, string[][]>>({});
  const [connected, setConnected] = useState(false);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);

  useEffect(() => {
    api.get('/category-ops').then((res) => { setTabs(res.data.tabs || {}); setConnected(res.data.connected); setSyncedAt(res.data.synced_at); }).catch(() => setConnected(false));
  }, []);

  // All figures below are derived directly from the live "3. PO Tracker" tab (real purchase
  // order line items) — no fabricated designer names or revenue splits.
  const rows = tabs[PO_TAB] || [];
  const header = rows[0] || [];
  const dataRows = rows.slice(1).filter((r) => r.some((c) => c));
  const supplierIdx = colIdx(header, 'Supplier');
  const totalIdx = colIdx(header, 'Total ₹ (incl GST)');
  const pendingQtyIdx = colIdx(header, 'Pending Qty');
  const statusIdx = colIdx(header, 'Production Status');

  const totalPoValue = useMemo(() => dataRows.reduce((sum, r) => sum + (totalIdx >= 0 ? parseCurrency(r[totalIdx]) : 0), 0), [dataRows, totalIdx]);
  const totalPendingQty = useMemo(() => dataRows.reduce((sum, r) => sum + (pendingQtyIdx >= 0 ? parseCurrency(r[pendingQtyIdx]) : 0), 0), [dataRows, pendingQtyIdx]);

  const supplierBreakdown = useMemo(() => {
    if (supplierIdx < 0 || totalIdx < 0) return [];
    const m: Record<string, number> = {};
    dataRows.forEach((r) => { const s = (r[supplierIdx] || 'Unknown').trim(); m[s] = (m[s] || 0) + parseCurrency(r[totalIdx]); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name, value: Math.round(value) }));
  }, [dataRows, supplierIdx, totalIdx]);

  const statusBreakdown = useMemo(() => {
    if (statusIdx < 0) return [];
    const m: Record<string, number> = {};
    dataRows.forEach((r) => { const s = (r[statusIdx] || 'Unspecified').trim() || 'Unspecified'; m[s] = (m[s] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value }));
  }, [dataRows, statusIdx]);

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="category-ops-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">
            Category & Purchase Ops
          </span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">
            Category Ops Sheet
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">
            Direct sync of the live PO Tracker, working capital, and inventory pulse tabs — purchase order value, supplier spend, and production status, as filed.
          </p>
        </div>
        <div className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'}`} data-testid="category-ops-connection-badge">
          <FileSpreadsheet className="h-4.5 w-4.5" />
          <span>{connected ? `Synced ${syncedAt ? formatIST(syncedAt) : ''}` : 'Not connected'}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          platform="Purchase Orders"
          title="Active PO Line Items"
          value={String(dataRows.length)}
          change="3. PO Tracker tab"
          trend="neutral"
          insight={{ title: 'PO Tracker', metric: 'Line Items', value: String(dataRows.length), aiInsight: `${dataRows.length} PO line items are currently tracked.`, prediction: 'Trend requires more sync history to project.', nextSteps: ['Review supplier spend below.'] }}
          onHover={onHover}
        />
        <MetricCard
          platform="Purchase Orders"
          title="Total PO Value (incl. GST)"
          value={totalIdx >= 0 ? inr(totalPoValue) : 'N/A'}
          change={`${dataRows.length} line items`}
          trend="neutral"
          insight={{ title: 'Total PO Value', metric: 'PO Value', value: totalIdx >= 0 ? inr(totalPoValue) : 'N/A', aiInsight: totalIdx >= 0 ? `${inr(totalPoValue)} committed across all active PO line items.` : "The 'Total ₹ (incl GST)' column was not found in the sheet.", prediction: 'Trend requires more sync history to project.', nextSteps: ['See Supplier Spend chart below for the breakdown.'] }}
          onHover={onHover}
        />
        <MetricCard
          platform="Fulfillment"
          title="Pending Qty (Units)"
          value={pendingQtyIdx >= 0 ? totalPendingQty.toLocaleString('en-IN') : 'N/A'}
          change="Across open POs"
          trend="neutral"
          insight={{ title: 'Pending Qty', metric: 'Units Pending', value: pendingQtyIdx >= 0 ? totalPendingQty.toLocaleString('en-IN') : 'N/A', aiInsight: pendingQtyIdx >= 0 ? `${totalPendingQty.toLocaleString('en-IN')} units are still pending delivery across open POs.` : "The 'Pending Qty' column was not found in the sheet.", prediction: 'Trend requires more sync history to project.', nextSteps: ['Cross-check against Inventory Pulse tab for stockout risk.'] }}
          onHover={onHover}
        />
        <MetricCard
          platform="Sheet Tabs"
          title="Tabs Synced"
          value={String(Object.keys(tabs).length)}
          change="Category Ops sheet"
          trend="neutral"
          insight={{ title: 'Synced Tabs', metric: 'Tabs', value: String(Object.keys(tabs).length), aiInsight: `${Object.keys(tabs).length} tabs synced, including WC Summary, Inventory Pulse, PO Tracker, and Vendor Dues.`, prediction: 'Trend requires more sync history to project.', nextSteps: ['Browse each tab directly in the live sheet viewer below.'] }}
          onHover={onHover}
        />
      </div>

      {(supplierBreakdown.length > 0 || statusBreakdown.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm lg:col-span-2 space-y-4" data-testid="category-ops-supplier-chart">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block pb-1 border-b border-slate-100">Top Suppliers by PO Value</span>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={supplierBreakdown} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" stroke="#94a3b8" fontSize={10} tickLine={false} tickFormatter={(v) => inr(v)} />
                  <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={10} tickLine={false} width={110} />
                  <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} formatter={(v: any) => [inr(v), 'PO Value']} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {supplierBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4" data-testid="category-ops-status-chart">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block pb-1 border-b border-slate-100">Production Status</span>
            <div className="h-52 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusBreakdown} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={3}>
                    {statusBreakdown.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.name] || COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} formatter={(v: any, n: any) => [`${v} POs`, n]} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <RecentChangesPanel apiPath="/category-ops/changes" label="Category Ops" />

      <SheetDataViewer apiPath="/category-ops" label="Category Ops" changesApiPath="/category-ops/changes" />
    </div>
  );
}
